// Chat: the conversation list (teams with their channels, then direct and group chats) and the open conversation.
import { useState, type FormEvent } from "react";
import { Link, NavLink, Outlet, useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatShort, keys, plainText, type ConversationSummary, type Presence, type SearchResult, type TeamSummary } from "../../lib/chat";
import { Avatar, Button, Spinner, cx } from "../../components/ui";
import { Icon } from "../../components/icons";
import { atLeast, errorText, useOrg } from "../org";
import { BrowseTeamsDialog, CreateTeamDialog, NewChatDialog, PresenceDot, TeamDialog, usePresence } from "./dialogs";

export { ConversationView } from "./conversation";

export function ChatLayout() {
  const ctx = useOrg();
  const { conversationId } = useParams();
  return (
    <div className="flex h-full">
      <aside className={cx("w-full shrink-0 flex-col border-r border-slate-200 bg-white md:flex md:w-80 dark:border-slate-800 dark:bg-slate-900", conversationId ? "hidden" : "flex")}>
        <Sidebar />
      </aside>
      <section className={cx("min-w-0 flex-1 flex-col", conversationId ? "flex" : "hidden md:flex")}>
        <Outlet context={ctx} />
      </section>
    </div>
  );
}

export function ChatHome() {
  const [open, setOpen] = useState(false);
  return (
    <div className="grid h-full place-items-center p-6 text-center">
      <div>
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-brand-700/30"><Icon name="chat" className="h-7 w-7" /></div>
        <h1 className="text-lg font-semibold">Pick a conversation</h1>
        <p className="mt-1 text-sm text-slate-500">Or start a new one with someone in your organization.</p>
        <Button className="mt-4" onClick={() => setOpen(true)}><Icon name="edit" className="h-4 w-4" />New chat</Button>
      </div>
      <NewChatDialog open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function Sidebar() {
  const { org, role, me } = useOrg();
  const [dialog, setDialog] = useState<"new" | "createTeam" | "browseTeams" | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<string | null>(null);
  const presence = usePresence();
  const conversations = useQuery({ queryKey: keys.conversations(org.slug), queryFn: () => api<{ conversations: ConversationSummary[] }>(`/orgs/${org.slug}/conversations`) });
  const teams = useQuery({ queryKey: keys.teams(org.slug), queryFn: () => api<{ teams: TeamSummary[] }>(`/orgs/${org.slug}/teams`) });

  const all = conversations.data?.conversations ?? [];
  const myTeams = (teams.data?.teams ?? []).filter((t) => t.myRole);
  const chats = all.filter((c) => c.kind !== "channel");
  const projectChats = all.filter((c) => c.kind === "channel" && c.project).sort((a, b) => a.title.localeCompare(b.title));
  const channelsOf = (teamId: string) => all.filter((c) => c.kind === "channel" && c.team?.id === teamId).sort((a, b) => (a.isGeneral ? -1 : b.isGeneral ? 1 : (a.name ?? "").localeCompare(b.name ?? "")));

  function submitSearch(e: FormEvent) {
    e.preventDefault();
    const q = search.trim();
    setQuery(q.length >= 2 ? q : null);
  }

  return (
    <>
      <div className="flex items-center justify-between px-4 pb-2 pt-4">
        <h1 className="text-lg font-semibold">Chat</h1>
        <div className="flex gap-1">
          <button type="button" onClick={() => setDialog("browseTeams")} title="Teams" aria-label="Teams" className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"><Icon name="team" className="h-5 w-5" /></button>
          <button type="button" onClick={() => setDialog("new")} title="New chat" aria-label="New chat" className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"><Icon name="edit" className="h-5 w-5" /></button>
        </div>
      </div>
      <form onSubmit={submitSearch} className="px-4 pb-3" role="search">
        <div className="relative">
          <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); if (!e.target.value) setQuery(null); }} placeholder="Search messages" aria-label="Search messages"
            className="block w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm placeholder:text-slate-400 focus:bg-white dark:border-slate-700 dark:bg-slate-800 dark:focus:bg-slate-900" />
        </div>
      </form>

      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        {query ? <SearchResults q={query} onClose={() => { setQuery(null); setSearch(""); }} /> : (
          <>
            {conversations.isLoading && <div className="flex justify-center p-6 text-brand-600"><Spinner /></div>}

            {/* A guest (a project's client, say) can't browse or make teams: no Teams section unless they're in one. */}
            {(atLeast(role, "member") || myTeams.length > 0) && <SectionTitle label="Teams" action={atLeast(role, "member") ? { label: "Create a team", onClick: () => setDialog("createTeam") } : undefined} />}
            {myTeams.length === 0 && !teams.isLoading && atLeast(role, "member") && (
              <p className="px-4 pb-2 text-sm text-slate-500">
                You're not in any teams. <button type="button" onClick={() => setDialog("browseTeams")} className="text-brand-600 hover:underline">Browse teams</button>
              </p>
            )}
            {myTeams.map((t) => (
              <div key={t.id} className="mb-1">
                <div className="group flex items-center gap-1 px-2">
                  <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [t.id]: !c[t.id] }))} aria-expanded={!collapsed[t.id]}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800">
                    <Icon name={collapsed[t.id] ? "chevronRight" : "chevron"} className="h-4 w-4 shrink-0 text-slate-400" />
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-brand-50 text-[10px] font-bold text-brand-700 dark:bg-brand-700/30 dark:text-brand-100">{t.name.slice(0, 2).toUpperCase()}</span>
                    <span className="truncate">{t.name}</span>
                  </button>
                  <button type="button" onClick={() => setTeamId(t.id)} aria-label={`Manage ${t.name}`} title="Channels, people and settings"
                    className="rounded-md p-1.5 text-slate-400 opacity-0 hover:bg-slate-100 hover:text-slate-700 focus:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100 dark:hover:bg-slate-800"><Icon name="more" className="h-4 w-4" /></button>
                </div>
                {!collapsed[t.id] && (
                  <ul>
                    {channelsOf(t.id).map((c) => <ConversationRow key={c.id} c={c} indent />)}
                    <li><button type="button" onClick={() => setTeamId(t.id)} className="ml-9 rounded-md px-2 py-1 text-xs text-slate-500 hover:text-brand-600">More channels…</button></li>
                  </ul>
                )}
              </div>
            ))}

            {projectChats.length > 0 && (
              <>
                <SectionTitle label="Projects" />
                <ul className="mb-1">{projectChats.map((c) => <ConversationRow key={c.id} c={c} indent icon="board" />)}</ul>
              </>
            )}

            <SectionTitle label="Chats" action={{ label: "New chat", onClick: () => setDialog("new") }} />
            {chats.length === 0 && !conversations.isLoading && <p className="px-4 text-sm text-slate-500">No chats yet. Start one with the pencil above.</p>}
            <ul>
              {chats.map((c) => {
                const other = c.kind === "dm" ? c.members.find((m) => m.id !== me.user.id) ?? c.members[0] : undefined;
                return <ConversationRow key={c.id} c={c} presence={other ? presence[other.id] : undefined} otherId={other?.id} />;
              })}
            </ul>
          </>
        )}
      </div>

      <NewChatDialog open={dialog === "new"} onClose={() => setDialog(null)} />
      <CreateTeamDialog open={dialog === "createTeam"} onClose={() => setDialog(null)} />
      <BrowseTeamsDialog open={dialog === "browseTeams"} onClose={() => setDialog(null)} onCreate={() => setDialog("createTeam")} onManage={setTeamId} />
      <TeamDialog teamId={teamId} onClose={() => setTeamId(null)} />
    </>
  );
}

function SectionTitle({ label, action }: { label: string; action?: { label: string; onClick: () => void } }) {
  return (
    <div className="flex items-center justify-between px-4 pb-1 pt-3">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</h2>
      {action && <button type="button" onClick={action.onClick} aria-label={action.label} title={action.label} className="rounded-md p-2 [@media(pointer:coarse)]:p-3 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"><Icon name="plus" className="h-4 w-4" /></button>}
    </div>
  );
}

function ConversationRow({ c, indent, presence, otherId, icon }: { c: ConversationSummary; indent?: boolean; presence?: Presence; otherId?: string; icon?: "board" }) {
  const { org } = useOrg();
  const unread = c.unread > 0 && !c.muted;
  const preview = c.lastMessage ? (c.lastMessage.deleted ? "Message deleted" : c.lastMessage.kind === "call" ? "Call" : `${c.kind !== "dm" && c.lastMessage.authorName ? `${c.lastMessage.authorName.split(" ")[0]}: ` : ""}${plainText(c.lastMessage.body, new Map(c.members.map((m) => [m.id, m.name])))}` || "Attachment") : "";
  return (
    <li>
      <NavLink to={`/o/${org.slug}/chat/${c.id}`}
        className={({ isActive }) => cx("mx-2 flex items-center gap-3 rounded-lg px-2 py-2", indent && "py-1.5 pl-9", isActive ? "bg-brand-50 dark:bg-brand-700/25" : "hover:bg-slate-100 dark:hover:bg-slate-800")}>
        {indent ? (
          <Icon name={icon ?? (c.isPrivate ? "lock" : "hash")} className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <span className="relative shrink-0">
            {c.kind === "group" ? <span className="grid h-9 w-9 place-items-center rounded-full bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-200"><Icon name="users" className="h-4 w-4" /></span> : <Avatar name={c.title} userId={otherId} />}
            {c.kind === "dm" && <PresenceDot status={presence} userId={otherId} className="absolute -bottom-0.5 -right-0.5" />}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className={cx("truncate text-sm", unread ? "font-semibold" : "font-medium", c.muted && "text-slate-500")}>{c.title}</span>
            {!indent && c.lastMessage && <span className="ml-auto shrink-0 text-xs text-slate-400">{formatShort(c.lastMessage.createdAt)}</span>}
          </span>
          {!indent && preview && <span className={cx("block truncate text-xs", unread ? "text-slate-800 dark:text-slate-200" : "text-slate-500")}>{preview}</span>}
        </span>
        {c.muted && <Icon name="bellOff" className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
        {unread && <span className="shrink-0 rounded-full bg-brand-600 px-1.5 text-[11px] font-semibold leading-5 text-white">{c.unread > 99 ? "99+" : c.unread}</span>}
      </NavLink>
    </li>
  );
}

// Search hits: the matched words come back between << and >> (rendered as highlights, never as HTML).
function SearchResults({ q, onClose }: { q: string; onClose: () => void }) {
  const { org } = useOrg();
  const navigate = useNavigate();
  const results = useQuery({ queryKey: ["search", org.slug, q], queryFn: () => api<{ results: SearchResult[] }>(`/orgs/${org.slug}/search?q=${encodeURIComponent(q)}`) });
  return (
    <div>
      <div className="flex items-center justify-between px-4 pb-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Results for “{q}”</h2>
        <button type="button" onClick={onClose} className="text-xs text-brand-600 hover:underline">Clear</button>
      </div>
      {results.isLoading && <div className="flex justify-center p-6 text-brand-600"><Spinner /></div>}
      {results.error && <p className="px-4 text-sm text-red-600">{errorText(results.error)}</p>}
      {results.data?.results.length === 0 && <p className="px-4 text-sm text-slate-500">No messages match.</p>}
      <ul>
        {results.data?.results.map((r) => (
          <li key={r.messageId}>
            <button type="button" onClick={() => navigate(`/o/${org.slug}/chat/${r.conversationId}${r.parentId ? `?thread=${r.parentId}` : ""}`)}
              className="mx-2 block w-[calc(100%-1rem)] rounded-lg px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-800">
              <span className="flex items-baseline gap-2 text-xs text-slate-500"><span className="truncate font-medium text-slate-700 dark:text-slate-300">{r.conversationTitle}</span><span className="ml-auto shrink-0">{formatShort(r.createdAt)}</span></span>
              <span className="mt-0.5 block text-sm"><span className="font-medium">{r.author}: </span><Highlighted text={plainText(r.snippet, new Map())} /></span>
            </button>
          </li>
        ))}
      </ul>
      <p className="px-4 pt-2 text-xs text-slate-400"><Link to={`/o/${org.slug}/chat`} onClick={onClose} className="hover:underline">Back to conversations</Link></p>
    </div>
  );
}

function Highlighted({ text }: { text: string }) {
  const parts = text.split(/(<<.*?>>)/g);
  return <>{parts.map((p, i) => (p.startsWith("<<") && p.endsWith(">>") ? <mark key={i} className="rounded bg-amber-100 px-0.5 text-inherit dark:bg-amber-500/30">{p.slice(2, -2)}</mark> : <span key={i}>{p}</span>))}</>;
}
